import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PayrollService } from '@/services/payroll.service';
import { Benefit, BenefitType } from '@/models/payroll.model';
import { HRService } from '@/services/hr.service';

export const BenefitForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;
  const employeeId = searchParams.get('employeeId') || '';

  const [formData, setFormData] = useState<Partial<Benefit>>({
    employeeId,
    type: BenefitType.health,
    coverageLevel: 'individual',
    employeeContribution: 0,
    employerContribution: 0,
    totalCost: 0,
    effectiveDate: new Date(),
    enrollmentDate: new Date(),
    status: 'active',
    dependents: [],
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: benefit, isLoading } = useQuery({
    queryKey: ['benefit', id],
    queryFn: () => id ? PayrollService.getBenefitById(id) : null,
    enabled: isEditing && !!id,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => HRService.getAllEmployees(),
  });

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);

  React.useEffect(() => {
    if (benefit) {
      setFormData(benefit);
    }
  }, [benefit]);

  React.useEffect(() => {
    // Auto-calculate total cost
    const total = (formData.employeeContribution || 0) + (formData.employerContribution || 0);
    setFormData(prev => ({
      ...prev,
      totalCost: total,
    }));
  }, [formData.employeeContribution, formData.employerContribution]);

  const mutation = useMutation({
    mutationFn: async (data: Partial<Benefit>) => {
      if (isEditing && id) {
        await PayrollService.updateBenefit(id, data);
      } else {
        await PayrollService.createBenefit(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benefits'] });
      navigate('/payroll?tab=benefits');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save benefit');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.employeeId || !formData.effectiveDate || !formData.enrollmentDate) {
      setErrorMessage('Employee, Effective Date, and Enrollment Date are required');
      return;
    }

    if (selectedEmployee) {
      formData.employeeName = selectedEmployee.fullName;
    }

    mutation.mutate(formData);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Benefit' : 'New Benefit'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Employee *</label>
            <select
              required
              value={formData.employeeId}
              onChange={(e) => {
                const employeeId = e.target.value;
                const emp = employees.find(emp => emp.id === employeeId);
                setFormData({
                  ...formData,
                  employeeId,
                  employeeName: emp?.fullName || '',
                });
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Employee</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.fullName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Benefit Type *</label>
            <select
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as BenefitType })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(BenefitType).map(type => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Provider</label>
            <input
              type="text"
              value={formData.provider || ''}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value || undefined })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Blue Cross Blue Shield"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Plan Name</label>
            <input
              type="text"
              value={formData.planName || ''}
              onChange={(e) => setFormData({ ...formData, planName: e.target.value || undefined })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Premium Plan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Coverage Level *</label>
            <select
              required
              value={formData.coverageLevel}
              onChange={(e) => setFormData({ ...formData, coverageLevel: e.target.value as 'individual' | 'family' })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="individual">Individual</option>
              <option value="family">Family</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status *</label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'pending' | 'terminated' })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Effective Date *</label>
            <input
              type="date"
              required
              value={formData.effectiveDate ? new Date(formData.effectiveDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, effectiveDate: new Date(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Enrollment Date *</label>
            <input
              type="date"
              required
              value={formData.enrollmentDate ? new Date(formData.enrollmentDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, enrollmentDate: new Date(e.target.value) })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value ? new Date(e.target.value) : undefined })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">Cost Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Employee Contribution</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.employeeContribution || 0}
                onChange={(e) => setFormData({ ...formData, employeeContribution: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Employer Contribution</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.employerContribution || 0}
                onChange={(e) => setFormData({ ...formData, employerContribution: parseFloat(e.target.value) || 0 })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost (Auto-calculated)</label>
              <input
                type="number"
                step="0.01"
                value={formData.totalCost || 0}
                disabled
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 font-semibold"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Dependents (Optional)</label>
          <p className="mt-1 text-xs text-gray-500 mb-2">
            Enter dependent names separated by commas (e.g., John Doe, Jane Doe)
          </p>
          <input
            type="text"
            value={formData.dependents?.join(', ') || ''}
            onChange={(e) => {
              const dependents = e.target.value
                .split(',')
                .map(d => d.trim())
                .filter(d => d.length > 0);
              setFormData({ ...formData, dependents: dependents.length > 0 ? dependents : [] });
            }}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder="e.g., John Doe, Jane Doe"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/payroll?tab=benefits')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

